import { ListaStakeManagerReadAbi } from '../abis/lista'
import { TotalSupplyAbi } from '../abis/shared'
import type { LstRegistryEntry } from '../registry'
import { ONE_E18, toBigInt, type LstReader } from './shared'

/**
 * Lista slisBNB reader.
 *
 * Three calls: `slisBNB.totalSupply()` + `ListaStakeManager
 * .convertSnBnbToBnb(1e18)` (1e18-scaled BNB-per-slisBNB rate) +
 * `ListaStakeManager.getTotalPooledBnb()` (total BNB backing the
 * supply, used directly as `totalAssets`).
 *
 * The slisBNB token is a plain ERC-20; the rate-bearing StakeManager is
 * a separate contract — its address is pinned in
 * `entry.readerExtras.listaStakeManager`. Falls back to a 1.0
 * placeholder rate if the manager is absent.
 */
export const readerListaSlisBnb = (entry: LstRegistryEntry): LstReader => {
  const manager = entry.readerExtras?.listaStakeManager
  if (!manager) {
    return {
      calls: [{ address: entry.address, name: 'totalSupply', params: [] }],
      abis: [TotalSupplyAbi],
      parse: ([supply]) => {
        const totalSupply = toBigInt(supply)
        if (totalSupply === undefined) return undefined
        return {
          totalAssets: totalSupply,
          totalSupply,
          exchangeRate: ONE_E18,
        }
      },
    }
  }
  return {
    calls: [
      { address: entry.address, name: 'totalSupply', params: [] },
      { address: manager, name: 'convertSnBnbToBnb', params: [ONE_E18] },
      { address: manager, name: 'getTotalPooledBnb', params: [] },
    ],
    abis: [TotalSupplyAbi, ListaStakeManagerReadAbi, ListaStakeManagerReadAbi],
    parse: ([supply, rate, pooled]) => {
      const totalSupply = toBigInt(supply)
      const exchangeRate = toBigInt(rate)
      if (totalSupply === undefined || exchangeRate === undefined) {
        return undefined
      }
      // Prefer the canonical pooled-BNB total; derive from supply×rate
      // if the pooled getter failed.
      const pooledBnb = toBigInt(pooled)
      const totalAssets = pooledBnb ?? (totalSupply * exchangeRate) / ONE_E18
      return { totalAssets, totalSupply, exchangeRate }
    },
  }
}
